import { roundMoney } from "@/libs/money/round-money";
import { getCookie } from "cookies-next";
import { REFERRAL_CODE_KEY } from "src/constants/CookieValues";
import { ProductInfo } from "@/services/cart-service/types";
import { CurrencyIsoCode } from "@/enums";
import { OrderDraft, OrderItemV2 } from "@/models/orders/OrderDraft";
import { ProductPrice } from "@/api/product-api";
import { ProductUtil } from "@/utils/ProductUtil";
import { GameItem, ShippingType } from "@/models/orders/OrderModelV2";
import { OrderUtils } from "@/v2-components/pages/checkout/_utils/order-utils";
import {
  CHECKOUT_RESPONSE_HEADER,
  CHECKOUT_RESPONSE_JSON,
} from "@/libs/orders/checkout-draft-response";

const getShippingType = (order: {
  items: Array<any>;
  gameItems: Array<any>;
}): ShippingType => {
  if (order.gameItems.length > 0 && order.items.length > 0) {
    return ShippingType.Mixed;
  }
  if (order.gameItems.length > 0) {
    return ShippingType.Virtual;
  }
  return ShippingType.Physical;
};

const isVirtualShipping = (order: OrderDraft) => {
  return getShippingType(order) === ShippingType.Virtual;
};

/**
 * 카트 항목 → `OrderItemV2`. 현재 통화·국가로 **가격이 없으면 `null`** — 던지지 않는다.
 *
 * 건너뛸지 막을지는 **호출처가 정한다**: 장바구니 합계(렌더 중)는 건너뛰고(ghost 가 따로 막는다), 자체 결제
 * draft 는 멈추고(과소청구 방지), GE 계측은 건너뛴다. 종전엔 여기서 throw 해 렌더 중에 부른 장바구니가
 * 화면째 죽었다(#6119 · `docs/pitfalls.md` #80 「막는 장치로 둔 throw 도 호출처를 센다」).
 */
const generateOrderItem = ({
  product,
  currency,
  countryCode,
  furtherDiscountPercentile = 0,
}: {
  product: ProductInfo;
  currency: CurrencyIsoCode;
  countryCode?: string | null;
  furtherDiscountPercentile?: number;
}): OrderItemV2 | null => {
  // US usPrice / non-US prices 분기는 `ProductUtil.getProductPrice` 가 단일 출처.
  // 사양은 docs/pricing-tiers.md 참조.
  const price = ProductUtil.getProductPrice({
    currency,
    countryCode,
    product,
    furtherDiscountPercentile,
  });
  if (!price) return null;
  const skuName = ProductUtil.getSkuName(product, product.sku);
  const productImages = ProductUtil.getProductImages({
    product,
    sku: product.sku,
    options: product.options,
  });

  return {
    slug: product.slug,
    thumbnail: productImages?.[0] ?? product.thumbnail,
    sku: product.sku,
    qty: product.quantity,
    name: skuName ?? product.name ?? product.slug,
    options: product.options.map((o) => o.value).join(" / "),
    price: price.originalAmount,
    displayPrice: price.displayAmount || price.originalAmount,
    discount: roundMoney(price.discountAmount, currency),
  };
};

const generateOrderItems = (
  products: ProductInfo[],
  currency: CurrencyIsoCode,
  countryCode?: string | null,
): OrderItemV2[] => {
  // draft 를 만드는 경로라 가격 없는 항목을 떨구면 그만큼 덜 청구된다 — 멈춘다.
  return products.map((product) => {
    const orderItem = generateOrderItem({ product, currency, countryCode });
    if (!orderItem) {
      throw new Error(
        `Price not found for product ${product.slug} in currency ${currency}`,
      );
    }
    return orderItem;
  });
};

const generateOrderDraft = ({
  products,
  currency,
  gameItems,
  shippingCountry,
  shippingPrice,
}: {
  shippingPrice: number;
  products: ProductInfo[];
  shippingCountry: string;
  currency: CurrencyIsoCode;
  gameItems: GameItem[];
}): Partial<OrderDraft> => {
  const orderItems = generateOrderItems(products, currency, shippingCountry);
  const priceSummary = OrderUtils.getPriceSummary({
    gameItems,
    items: orderItems,
  });

  return {
    currency,
    // US orders include shipping and tax in the displayed price
    includeShippingAndTax: shippingCountry === "US",
    items: orderItems,
    gameItems,
    shippingCountry,
    shippingPrice,
    subtotalAmount: priceSummary.totalOriginalPrice,
    discountAmount: priceSummary.totalDiscountAmount,
    totalAmount: priceSummary.totalSalePrice + shippingPrice,
    paymentMethod: "",
  };
};

/**
 * `bh_rcode` 쿠키를 안전하게 디코드한다. 없거나 손상됐으면 **null**.
 *
 * ⚠️ **던지면 안 된다.** 이 값을 읽는 경로가 `useReferralDiscount` → `useCartWithProducts` ·
 * `useCheckout` · `useProductTags` 로 이어져 **PDP·카트·체크아웃 렌더 중에** 평가된다.
 * 손상된 쿠키 하나로 구매 표면 전체가 죽고, 에러 바운더리가 재시도해도 다음 렌더에서 또
 * 던지므로 복구되지 않는다. (쿠키는 `httpOnly: false` 라 클라가 언제든 망가뜨릴 수 있다.)
 *
 * 손상 = **할인 없음**으로 떨어뜨린다. 아무것도 읽을 수 없는 상태라 할인을 주지 않는 쪽이
 * 안전한 기본값이다. (아래 `toFiniteNumber` 는 반대로 관대한데, 그쪽은 값을 **읽을 수는 있고**
 * 타입만 다른 경우라 기존 동작을 유지하는 게 안전하다.)
 */
const parseReferralCookie = (): Record<string, unknown> | null => {
  const referralCodeValues = getCookie(REFERRAL_CODE_KEY);
  if (!referralCodeValues) {
    return null;
  }

  try {
    const parsed = JSON.parse(
      Buffer.from(referralCodeValues, "base64").toString("utf8"),
    );
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * 숫자 자리 값을 읽는다. **문자열 숫자도 허용**하고 그 외에는 0.
 *
 * ⚠️ `typeof !== "number"` 로 잘라내면 안 된다. 이 값들은 sdk-apis 응답(`any`)이 그대로 쿠키에
 * 실린 것이라 **API 가 `"0.1"` 을 주면 조용히 할인이 사라진다.** 기존 코드는 `?? 0` 으로 문자열을
 * 그대로 흘려보냈고 하위 산술에서 강제변환돼 **동작했다** — 엄격하게 바꾸는 쪽이 오히려 동작
 * 변경이다. 여기서는 그 강제변환을 명시적으로 한 자리에 모은다.
 *
 * (`code` 는 URL 파라미터에서 오므로 항상 문자열이라 엄격 검사로 충분하다.)
 */
const toFiniteNumber = (value: unknown): number => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
};

const tryGetCodeFromCookie = (): string => {
  const cc = parseReferralCookie();
  return typeof cc?.code === "string" ? cc.code : "";
};

const tryGetReferralDiscountFromCookie = (): number =>
  toFiniteNumber(parseReferralCookie()?.discountPercentile);

const tryGetReferralEndTimeFromCookie = (): number =>
  toFiniteNumber(parseReferralCookie()?.endTime);

const createOrderDraft = async (
  order: Partial<OrderDraft>,
  token?: string,
): Promise<{
  success: boolean;
  redirectUrl: string | null;
  code?: string;
  error?: string;
}> => {
  try {
    console.log("createOrderDraft", order);
    const referralCode = tryGetCodeFromCookie();

    const response = await fetch(`/api/order/checkout/`, {
      method: "post",
      headers: {
        "content-type": "application/json",
        authorization: token ? "Bearer " + token : "",
        // 성공 시 307 대신 `{ redirectUrl }` 을 받는다 — 307 을 따라가면 결제 페이지를 POST 로 한 번 더
        // SSR 하고 버린다(#6125 · 약 3s). `src/libs/orders/checkout-draft-response.ts`
        [CHECKOUT_RESPONSE_HEADER]: CHECKOUT_RESPONSE_JSON,
      },
      // 예전 서버(롤백)는 여전히 307 을 준다 — 그때도 결제 페이지로 갈 수 있게 따라가고 아래에서 `response.url` 을 읽는다.
      redirect: "follow",
      body: JSON.stringify({ ...order, referralCode }),
    });

    // Check if the response is ok (status in the range 200-299)
    if (!response.ok) {
      // 서버 가드(비배송국 NON_SHIPPABLE_COUNTRY / 음수 배송비 등)가 반환한 code 를
      // 노출해 호출처가 사용자에게 사유를 안내할 수 있게 한다. (기존엔 throw → code 유실)
      let code: string | undefined;
      try {
        const errBody = await response.json();
        code = errBody?.code;
      } catch {
        // 바디 파싱 실패는 무시 — code 없이 일반 실패로 처리.
      }
      return { success: false, code, redirectUrl: null };
    }

    if (response.redirected) {
      return { success: true, redirectUrl: response.url };
    }

    let redirectUrl: string | null = null;
    try {
      const okBody = await response.json();
      if (typeof okBody?.redirectUrl === "string" && okBody.redirectUrl) {
        redirectUrl = okBody.redirectUrl;
      }
    } catch {
      // 본문이 JSON 이 아니면 경로를 모른다 — 호출처가 일반 실패로 처리한다(종전 `redirected === false` 와 같다).
    }
    return { success: true, redirectUrl };
  } catch (error: any) {
    // Handle network errors or other exceptions
    return {
      success: false,
      error: error.message,
      redirectUrl: null,
    };
  }
};

/**
 * 결제 직전 저장이 「이미 전환된 draft」로 거절됐다 — 다른 탭·기기에서 결제가 끝나 주문이 됐다. 일반 실패(null)와
 * 가른다: 호출부는 결제를 시작하지 않고 「이미 주문됨」을 보여준다(docs/payments/Inicis.md §3-2).
 */
export const SAVE_DRAFT_ALREADY_CONVERTED = "already-converted" as const;

/**
 * mgmt `OrderDraftService.REASON_DRAFT_ALREADY_CONVERTED` — 409 응답의 `errorMessage` 로 그대로 온다.
 * ⚠️ mgmt 와 같은 문자열이어야 한다. 바꾸면 양쪽을 함께 바꾼다(mgmt docs/payment-transaction-idempotency.md §2-1).
 */
const MGMT_REASON_DRAFT_ALREADY_CONVERTED = "draft-already-converted";

/**
 * 결제 직전 저장이 **쿠폰 때문에** 거절됐다 — 보상판매 쿠폰(#6101)은 이 저장에서 그 draft 로 15분 예약된다(mgmt
 * `TradeInPaymentService.reserveForPayment` · mgmt docs/trade-in.md 「결제 예약」). 청구 전이라 결제를 시작하지 않고,
 * 호출부가 띄우는 실패 다이얼로그가 사유에 맞는 안내를 보인다.
 *
 * - `COUPON_RESERVED` — 같은 쿠폰으로 다른 draft 가 결제 중이다(다른 탭, 또는 그만두고 쿠폰을 바꾼 직전 결제). 예약이 끝나면
 *   (최대 15분) 다시 된다.
 * - `COUPON_UNUSABLE` — 쿠폰이 이미 쓰였거나 기한이 지났다. 쿠폰을 빼고 다시 해야 한다.
 */
export const SAVE_DRAFT_COUPON_RESERVED = "coupon-reserved" as const;
export const SAVE_DRAFT_COUPON_UNUSABLE = "coupon-unusable" as const;

/**
 * mgmt 의 거절 사유 — `errorMessage` 로 그대로 온다. ⚠️ mgmt 와 같은 문자열이어야 한다. 셋 다 이 저장이 부르는
 * `TradeInPaymentService.reserve` 에서 난다(409 는 `REASON_COUPON_RESERVED`, 400 두 개는 그 함수의 `IllegalArgumentException`
 * 한국어 원문 — mgmt docs/trade-in.md 「결제 예약」 표). `ShopCouponService.requireUsable` 에도 같은 문구가 있지만 그쪽은
 * draft **생성**에서만 불린다 — 문구를 대조할 때 그쪽을 보지 않는다. 같은 함수의 `쿠폰을 찾을 수 없습니다`(예약 직전에 쿠폰
 * 문서가 사라지는 경합)는 일부러 넣지 않았다 — 일반 실패로 둔다.
 */
const MGMT_REASON_COUPON_RESERVED = "coupon-reserved";
const MGMT_REASON_COUPON_UNUSABLE: ReadonlySet<string> = new Set([
  "이미 사용한 쿠폰입니다",
  "유효기간이 지난 쿠폰입니다",
]);

const saveOrderDraft = async (
  order: OrderDraft,
  token?: string,
): Promise<
  | OrderDraft
  | null
  | typeof SAVE_DRAFT_ALREADY_CONVERTED
  | typeof SAVE_DRAFT_COUPON_RESERVED
  | typeof SAVE_DRAFT_COUPON_UNUSABLE
> => {
  try {
    const res = await fetch(`/admin-api/api/v2/orders/drafts/${order.id}/`, {
      method: "put",
      headers: {
        "content-type": "application/json",
        authorization: token ? "Bearer " + token : "",
      },
      body: JSON.stringify({ ...order }),
    });
    const jsoned = await res.json();
    if (res.ok) {
      return jsoned.message;
    }
    if (
      res.status === 409 &&
      jsoned?.errorMessage === MGMT_REASON_DRAFT_ALREADY_CONVERTED
    ) {
      return SAVE_DRAFT_ALREADY_CONVERTED;
    }
    if (
      res.status === 409 &&
      jsoned?.errorMessage === MGMT_REASON_COUPON_RESERVED
    ) {
      return SAVE_DRAFT_COUPON_RESERVED;
    }
    if (
      res.status === 400 &&
      MGMT_REASON_COUPON_UNUSABLE.has(jsoned?.errorMessage)
    ) {
      return SAVE_DRAFT_COUPON_UNUSABLE;
    }
    console.log("jsoned", jsoned);
  } catch (error) {
    console.error(error);
  }
  return null;
};

const toProductPrice = (
  item: OrderItemV2,
  currency: CurrencyIsoCode,
): ProductPrice => ({
  currency,
  discountAmount: item.discount,
  displayAmount: item.displayPrice,
  originalAmount: item.price,
});

const OrderService = {
  isVirtualShipping,
  getShippingType,
  generateOrderItem,
  generateOrderItems,
  generateOrderDraft,
  createOrderDraft,
  saveOrderDraft,
  tryGetCodeFromCookie,
  tryGetReferralDiscountFromCookie,
  tryGetReferralEndTimeFromCookie,
  toProductPrice,
} as const;

export default OrderService;
